export type { AdminAiCall, AdminAiFilter, AdminAiCallRow, AdminAiBreakdown, AdminAiOverview, AdminAiInspection } from './types/platformAdminAi.js';
/**
 * Mesterplan API client — the single TS surface every web + mobile cluster uses
 * to talk to the .NET API. Replaces direct `firebase/firestore` SDK access at
 * Phase 4 cutover.
 *
 * Construct one instance per auth session and pass it down via context:
 *
 *     const api = new MesterplanApi({
 *       baseUrl: "https://api.mesterplan.dk",
 *       credentials: "include",
 *     });
 *     await api.users.me();
 *
 * Endpoint modules live under `src/endpoints/*.ts`. Each module exports a
 * cluster of methods (one per .NET API endpoint). Type definitions in
 * `src/types/*.ts` mirror the .NET DTOs.
 */

import { ActivityApi } from "./endpoints/activity.js";
import { DashboardApi } from "./endpoints/dashboard.js";
import { AftalesedlerApi } from "./endpoints/aftalesedler.js";
import { AssignmentsApi } from "./endpoints/assignments.js";
import { AssetsApi } from "./endpoints/assets.js";
import { PropertiesApi } from "./endpoints/properties.js";
import { BidParticipantsApi } from "./endpoints/bid-participants.js";
import { BilagApi } from "./endpoints/bilag.js";
import { BlueprintsApi } from "./endpoints/blueprints.js";
import { AnalysisDraftsApi } from "./endpoints/analysis-drafts.js";
import { TendersApi } from "./endpoints/tenders.js";
import { BlueprintCreditsApi } from "./endpoints/blueprintCredits.js";
import { KlsChecklistsApi } from "./endpoints/kls-checklists.js";
import { KlsCompetenciesApi } from "./endpoints/kls-competencies.js";
import { KlsDeviationsApi } from "./endpoints/kls-deviations.js";
import { KlsEquipmentApi } from "./endpoints/kls-equipment.js";
import { KlsEvaluationsApi } from "./endpoints/kls-evaluations.js";
import { KlsInspectionsApi } from "./endpoints/kls-inspections.js";
import { KsProjectItemsApi } from "./endpoints/ks-project-items.js";
import { LeaveApi } from "./endpoints/leave.js";
import { KsTemplatesApi } from "./endpoints/ks-templates.js";
import { CalculationTemplatesApi } from "./endpoints/calculation-templates.js";
import { VerificationTemplatesApi } from "./endpoints/verification-templates.js";
import { VerificationsApi } from "./endpoints/verifications.js";
import { CommunicationMessagesApi } from "./endpoints/communication-messages.js";
import { RecipientListsApi } from "./endpoints/recipient-lists.js";
import { SupportApi } from "./endpoints/support.js";
import { CompaniesApi } from "./endpoints/companies.js";
import { CreditorInvoicesApi } from "./endpoints/creditor-invoices.js";
import { CvrApi } from "./endpoints/cvr.js";
import { CustomSkillsApi } from "./endpoints/custom-skills.js";
import { CustomersApi } from "./endpoints/customers.js";
import { CustomerScoresApi } from "./endpoints/customer-scores.js";
import { DefectsApi } from "./endpoints/defects.js";
import { GuestsApi } from "./endpoints/guests.js";
import { IntegrationsApi } from "./endpoints/integrations.js";
import { InvoicesApi } from "./endpoints/invoices.js";
import { ProjectProgressInvoicesApi } from "./endpoints/project-progress-invoices.js";
import { KlsAiApi } from "./endpoints/kls-ai.js";
import { KlsTasksApi } from "./endpoints/kls-tasks.js";
import { MaterialsApi } from "./endpoints/materials.js";
import { ShoppingListApi } from "./endpoints/shopping-list.js";
import { MeApi } from "./endpoints/me.js";
import { AnnouncementsApi } from "./endpoints/announcements.js";
import { NotificationsApi } from "./endpoints/notifications.js";
import { PartnerInvitationsApi } from "./endpoints/partner-invitations.js";
import { PaymentTermsPresetsApi } from "./endpoints/payment-terms-presets.js";
import { PayrollApi } from "./endpoints/payroll.js";
import { PermissionsApi } from "./endpoints/permissions.js";
import { SupplierAccessApi } from "./endpoints/supplier-access.js";
import { DocumentsApi } from "./endpoints/documents.js";
import { FinancialsApi } from "./endpoints/financials.js";
import { PhotosApi } from "./endpoints/photos.js";
import { PriceLibraryApi } from "./endpoints/price-library.js";
import { PriceListsApi } from "./endpoints/price-lists.js";
import { ProductsApi } from "./endpoints/products.js";
import { ProjectBudgetsApi } from "./endpoints/project-budgets.js";
import { CaseEconomyApi } from "./endpoints/case-economy.js";
import { CompanyEconomyApi } from "./endpoints/company-economy.js";
import { ProjectCalculationsApi } from "./endpoints/project-calculations.js";
import { ProjectTilbudslisteApi } from "./endpoints/project-tilbudsliste.js";
import { ProjectsApi } from "./endpoints/projects.js";
import { QuotesApi } from "./endpoints/quotes.js";
import { QuoteTemplatesApi } from "./endpoints/quote-templates.js";
import { CustomReportsApi } from "./endpoints/custom-reports.js";
import { DpaApi } from "./endpoints/dpa.js";
import { SupplierApi } from "./endpoints/supplier.js";
import { SupplierCatalogApi } from "./endpoints/supplier-catalog.js";
import { CatalogOffersApi } from "./endpoints/catalog-offers.js";
import { GoalsApi } from "./endpoints/goals.js";
import { SettingsApi } from "./endpoints/settings.js";
import { TeamsApi } from "./endpoints/teams.js";
import { TimeEntriesApi } from "./endpoints/time-entries.js";
import { TimeTypesApi } from "./endpoints/time-types.js";
import { TimeplanApi } from "./endpoints/timeplan.js";
import { TermsTemplatesApi } from "./endpoints/terms-templates.js";
import { TradesApi } from "./endpoints/trades.js";
import { UsersApi } from "./endpoints/users.js";
import { UserPayApi } from "./endpoints/user-pay.js";
import { WalkthroughTemplatesApi } from "./endpoints/walkthrough-templates.js";
import { WalkthroughsApi } from "./endpoints/walkthroughs.js";
import { HttpClient, HttpClientOptions } from "./http.js";
import { PlatformAdminApi } from "./endpoints/platformAdmin.js";
export { PlatformAdminApi } from "./endpoints/platformAdmin.js";
export type * from "./types/platformAdmin.js";

export type {
  ApiError,
  ApiResult,
  BinaryDocument,
  CompanyLockReason,
  CompanyLockedBody,
  HttpClientOptions,
  Method,
  RequestOptions,
} from "./http.js";
export { COMPANY_LOCKED_STATUS, HttpClient, ORG_NOT_AVAILABLE_CODE, isCompanyLockedBody, isOrgNotAvailableError } from "./http.js";

export * from "./types/activity.js";
export * from "./types/dashboard.js";
export type {
  RecipientListStatusDto,
  RecipientListTokenDto,
  RecipientDto,
  CustomerRecipientListDto,
  AddRecipientPayload,
  UpdateRecipientPayload,
} from "./endpoints/recipient-lists.js";
export type {
  SupportTicketType,
  SupportTicketDto,
  CreateSupportTicketPayload,
  RoadmapIssueDto,
} from "./endpoints/support.js";
export * from "./types/aftalesedler.js";
export * from "./types/assets.js";
export * from "./types/properties.js";
export * from "./types/kls-checklists.js";
export * from "./types/kls-competencies.js";
export * from "./types/kls-deviations.js";
export * from "./types/kls-equipment.js";
export * from "./types/kls-evaluations.js";
export * from "./types/kls-inspections.js";
export * from "./types/ks-project-items.js";
export * from "./types/ks-templates.js";
export * from "./types/guests.js";
export * from "./types/calculation-templates.js";
export * from "./types/verification-templates.js";
export * from "./types/verifications.js";
export * from "./types/companies.js";
export * from "./types/cvr.js";
export * from "./types/communication-messages.js";
export * from "./types/creditor-invoices.js";
export * from "./types/custom-skills.js";
export * from "./types/assignments.js";
export * from "./types/bid-participants.js";
export * from "./types/bilag.js";
export * from "./types/blueprints.js";
export * from "./types/tenders.js";
export * from "./types/blueprintCredits.js";
export * from "./types/customers.js";
export * from "./types/customer-scores.js";
export * from "./types/defects.js";
export * from "./types/invoices.js";
export * from "./types/leave.js";
export * from "./types/kls-ai.js";
export * from "./types/kls-tasks.js";
export * from "./types/materials.js";
export * from "./types/partner-invitations.js";
export * from "./types/permissions.js";
export * from "./types/supplier-access.js";
export * from "./types/documents.js";
export * from "./types/announcements.js";
export * from "./types/notifications.js";
export * from "./types/photos.js";
export * from "./types/price-library.js";
export * from "./types/shopping-list.js";
export * from "./types/price-lists.js";
export * from "./types/products.js";
export * from "./types/financials.js";
export * from "./types/project-budgets.js";
export * from "./types/case-economy.js";
export * from "./types/company-economy.js";
export * from "./types/project-calculations.js";
export * from "./types/project-tilbudsliste.js";
export * from "./types/projects.js";
export * from "./types/project-statuses.js";
export * from "./types/teams.js";
export * from "./types/quotes.js";
export * from "./types/custom-reports.js";
export * from "./types/goals.js";
export * from "./types/settings.js";
export * from "./types/time-entries.js";
export * from "./types/time-types.js";
export * from "./types/timeplan.js";
export * from "./types/trades.js";
export * from "./types/users.js";
export * from "./types/user-pay.js";
export * from "./types/walkthrough-templates.js";
export * from "./types/walkthroughs.js";
export * from "./types/project-progress-invoices.js";
export type {
  CreateBilagPayload,
  ListBilagPagedQuery,
  ListBilagQuery,
  RejectBilagPayload,
  UpdateBilagPayload,
} from "./endpoints/bilag.js";
export type {
  UploadBlueprintPayload,
} from "./endpoints/blueprints.js";
export { BlueprintCreditsApi } from "./endpoints/blueprintCredits.js";
export type {
  CreateAssignmentPayload,
  LinkAssignmentInvoicePayload,
  ListAssignmentsQuery,
  UpdateAssignmentPayload,
} from "./endpoints/assignments.js";
export type {
  CreateBidParticipantPayload,
  ListBidParticipantsQuery,
  UpdateBidParticipantPayload,
} from "./endpoints/bid-participants.js";
export type {
  CreateCustomerPayload,
  CreateCustomerServiceAgreementPayload,
  CustomerEmailPayload,
  CustomerFields,
  CustomerHourlyPricePayload,
  CustomerPartyPayload,
  ListCustomersQuery,
  CustomerListSortBy,
  UpdateCustomerPayload,
} from "./endpoints/customers.js";
export type {
  AddMaintenanceLogPayload,
  AssetDocumentPayload,
  AssetMaintenancePartPayload,
  CompleteAssetMaintenanceSchedulePayload,
  CreateAssetMaintenanceSchedulePayload,
  CreateAssetPayload,
  ListAssetsQuery,
  UpdateAssetMaintenanceSchedulePayload,
  UpdateAssetPayload,
} from "./endpoints/assets.js";
export type {
  CreatePropertyPayload,
  ListPropertiesQuery,
  PropertyUnitPayload,
  UpdatePropertyPayload,
} from "./endpoints/properties.js";
export type {
  CreateKlsChecklistPayload,
  KlsChecklistItemPayload,
  ListKlsChecklistsQuery,
  UpdateKlsChecklistPayload,
} from "./endpoints/kls-checklists.js";
export type {
  CreateKlsCompetencyProfilePayload,
  KlsCompetencyPayload,
  KlsCertificationPayload,
  ListKlsCompetencyProfilesQuery,
  UpdateKlsCompetencyProfilePayload,
} from "./endpoints/kls-competencies.js";
export type {
  AutoFillKlsInspectionPayload,
  GenerateKlsReportSummaryPayload,
  PreFillKlsVerificationPayload,
} from "./endpoints/kls-ai.js";
export type {
  CreateKlsDeviationPayload,
  UpdateKlsDeviationPayload,
  ListKlsDeviationsQuery,
} from "./endpoints/kls-deviations.js";
export type {
  CreateKlsEquipmentPayload,
  UpdateKlsEquipmentPayload,
} from "./endpoints/kls-equipment.js";
export type {
  CreateKlsEvaluationPayload,
  KlsEvaluationSectionPayload,
  KlsImprovementActionPayload,
  KlsYearStatsPayload,
  ListKlsEvaluationsQuery,
  UpdateKlsEvaluationPayload,
} from "./endpoints/kls-evaluations.js";
export type {
  CreateKlsInspectionPayload,
  KlsInspectionResponsePayload,
  ListKlsInspectionsQuery,
  UpdateKlsInspectionPayload,
} from "./endpoints/kls-inspections.js";
export type {
  AssignKsTemplatePayload,
  CompleteKsItemPayload,
  OverrideKsItemPayload,
} from "./endpoints/ks-project-items.js";
export type {
  CreateKsTemplatePayload,
  KsTemplateItemPayload,
  ListKsTemplatesQuery,
  UpdateKsTemplatePayload,
} from "./endpoints/ks-templates.js";
export type {
  CalculationCategoryInput,
  CalculationSubItemTemplateInput,
  CreateCalculationTemplatePayload,
  UpdateCalculationTemplatePayload,
} from "./endpoints/calculation-templates.js";
export type {
  CreateVerificationTemplatePayload,
  ListVerificationTemplatesQuery,
  UpdateVerificationTemplatePayload,
  VerificationTemplateSectionPayload,
  VerificationTemplateFieldPayload,
  VerificationValidationRulePayload,
} from "./endpoints/verification-templates.js";
export type {
  ApproveVerificationPayload,
  VerificationPayload,
} from "./endpoints/verifications.js";
export type {
  CreateCustomSkillPayload,
  ListCustomSkillsQuery,
} from "./endpoints/custom-skills.js";
export type {
  BootstrapCompanyPayload,
  ConvertPartnerToCompanyPayload,
  PartnerConversionStatusDto,
  CreateCompanyPayload,
  CompanyMemberSort,
  SearchCompanyMembersQuery,
  UpdateCompanyPayload,
  UpdateCompanyMemberPayload,
  UpdateMemberDetailsPayload,
} from "./endpoints/companies.js";
export type {
  CreateCommunicationMessagePayload,
  ListCommunicationMessagesQuery,
  UpdateCommunicationMessagePayload,
} from "./endpoints/communication-messages.js";
export type {
  AssignCreditorInvoiceProjectPayload,
  ListCreditorInvoicesPagedQuery,
} from "./endpoints/creditor-invoices.js";
export type {
  ContinueGuestDefectPayload,
  CreateBulkDefectsPayload,
  CreateBulkTradeAssignmentPayload,
  CreateDefectPayload,
  ResolveDefectPayload,
  ResolveGuestDefectPayload,
  UpdateDefectPayload,
} from "./endpoints/defects.js";
export type {
  AcceptConnectionPayload,
  AcceptConnectionResult,
  BookAndSendEconomicMobileInvoicePayload,
  EconomicInstallUrlDto,
  InitiateConnectionPayload,
  InitiateConnectionResult,
  IntegrationConnectionDto,
  IntegrationConnectionStatus,
  EconomicMobileBookInvoiceResponse,
  EconomicSyncStatsResult,
  PushAllCustomersResult,
  PushAllProductsResult,
  RegisterEconomicMobilePaymentPayload,
  RegisterEconomicMobilePaymentResponse,
  SendEconomicMobileBookedInvoicePayload,
  // Trackrs attention nudge ("Vi har brug for din hjælp her", M7a/M7b)
  DismissTrackrsAttentionPayload,
  TrackrsAttentionDto,
  TrackrsAttentionSuggestion,
  TrackrsAutoLinkedItem,
  TrackrsConflictItem,
  TrackrsMissingBudgetItem,
  TrackrsMissingCaseNumberItem,
  TrackrsMissingCustomerItem,
  TrackrsMissingStartDateItem,
  TrackrsRemoteDeletedItem,
  TrackrsSameProjectItem,
  TrackrsSameProjectState,
  // "Slå Trackrs til" from inside Mesterplan (case 3)
  EnableTrackrsErrorBody,
  EnableTrackrsPayload,
  EnableTrackrsResult,
  TrackrsCompanyPrefillDto,
  TrackrsEnableConflictDto,
  TrackrsModuleState,
  TrackrsOfferConnectionDto,
  TrackrsOfferDto,
} from "./endpoints/integrations.js";
export type {
  EmployeeMappingEntryDto,
  ExportPayrollPayload,
  MatchedPayrollEmployeeDto,
  PayPartCodeDto,
  PayPartCodeMappingDto,
  PayrollEmployeeDto,
  PayrollEmployeeMappingOverviewDto,
  PayrollExportDto,
  PayrollExportStatus,
  PayrollInstallUrlDto,
  PushPayPartsResultDto,
  SaveCodeMappingPayload,
  SaveEmployeeMappingsPayload,
  SaveEmployeeMappingsResultDto,
  SyncSalaryResultDto,
} from "./endpoints/payroll.js";
export type { ChangeFeedQuery } from "./endpoints/activity.js";
export type {
  AftalesedlLineItemPayload,
  CreateAftalesedlPayload,
  UpdateAftalesedlPayload,
} from "./endpoints/aftalesedler.js";
export type { ListInvoicesPagedQuery, RegisterPaymentPayload } from "./endpoints/invoices.js";
export type {
  CreateAftaleseddelLinePayload,
  CreatePositionLinePayload,
  CreateProjectProgressInvoicePayload,
  MarkProgressInvoicePaidPayload,
} from "./endpoints/project-progress-invoices.js";
export type {
  CreateLeaveEntryPayload,
  ListLeaveEntriesQuery,
  RejectLeaveEntryPayload,
  UpdateLeaveEntryPayload,
} from "./endpoints/leave.js";
export type {
  ChangeKlsTaskStatusPayload,
  OverrideKlsTaskPayload,
} from "./endpoints/kls-tasks.js";
export type {
  AddShoppingListItemPayload,
  UpdateShoppingListItemPayload,
} from "./endpoints/shopping-list.js";
export type {
  CreateMaterialPayload,
  UpdateMaterialPayload,
  MoveMaterialPayload,
  RepriceMaterialsPayload,
  RepriceMaterialsResult,
  ApproveMaterialPayload,
  RejectMaterialPayload,
  AutoLinkMaterialResult,
} from "./endpoints/materials.js";
export type {
  CreateProjectBudgetPayload,
  UpdateProjectBudgetPayload,
} from "./endpoints/project-budgets.js";
export type {
  CalculationPositionPayload,
  CreateProjectCalculationPayload,
  ListProjectCalculationsQuery,
  UpdateProjectCalculationPayload,
} from "./endpoints/project-calculations.js";
export type {
  CreateProjectTilbudslistePayload,
  ReplaceProjectTilbudslistePayload,
} from "./endpoints/project-tilbudsliste.js";
export type {
  CreatePriceLibraryItemPayload,
  ListPriceLibraryItemsQuery,
  PriceLibraryMaterialLinePayload,
  ReplacePriceLibraryMaterialLinesPayload,
  UpdatePriceLibraryItemPayload,
} from "./endpoints/price-library.js";
export * from "./endpoints/price-lists.js";
export type {
  CreateProductPayload,
  ListProductsQuery,
} from "./endpoints/products.js";
export type {
  AddProjectPartnerPayload,
  CreateProjectPayload,
  ListProjectsQuery,
  NotifyTimeplanCustomerPayload,
  UpdateProjectPayload,
  UpdateProjectPartnerPayload,
} from "./endpoints/projects.js";
export type {
  AcceptPartnerInvitationPayload,
  CreatePartnerInvitationPayload,
  ListPartnerInvitationsQuery,
  UpdatePartnerInvitationPermissionsPayload,
} from "./endpoints/partner-invitations.js";
export type {
  AnnouncementFeedQuery,
  MarkAnnouncementsReadPayload,
} from "./endpoints/announcements.js";
export type {
  DeleteCreditorInvoiceNotificationsPayload,
  ListNotificationsQuery,
  MarkAllNotificationsReadPayload,
} from "./endpoints/notifications.js";
export type { InviteAccountManagerPayload } from "./endpoints/supplier-access.js";
export type {
  SetRolePermissionsPayload,
  UpsertUserOverridePayload,
} from "./endpoints/permissions.js";
export type {
  CreateTradePayload,
  ListTradesQuery,
  PagedTrades,
} from "./endpoints/trades.js";
export type {
  CreatePaymentTermsPresetPayload,
  PaymentTermsPresetDto,
  UpdatePaymentTermsPresetPayload,
} from "./endpoints/payment-terms-presets.js";
export type {
  CreateTermsTemplatePayload,
  TermsTemplateDto,
  UpdateTermsTemplatePayload,
} from "./endpoints/terms-templates.js";
export type {
  CreateWalkthroughTemplatePayload,
  ListWalkthroughTemplatesQuery,
  UpdateWalkthroughTemplatePayload,
} from "./endpoints/walkthrough-templates.js";
export type {
  AppendWalkthroughItemPayload,
  CreateWalkthroughPayload,
  ListWalkthroughsQuery,
  UpdateWalkthroughPayload,
} from "./endpoints/walkthroughs.js";
export type {
  UploadDocumentPayload,
  CreateDocumentFolderPayload,
  UpdateDocumentMetadataPayload,
  ListDocumentsFilterParams,
} from "./endpoints/documents.js";
export type { EarningsRangeQuery } from "./endpoints/financials.js";
export type { UploadPhotoPayload } from "./endpoints/photos.js";
export type {
  CompanyLogoDto,
  RegisterNemHandelPayload,
  SetIntegrationConnectionPayload,
  UpdateCommunicationSettingsPayload,
} from "./endpoints/settings.js";
export type {
  UpdateCurrentUserPayload,
  UpdateUserPayload,
} from "./endpoints/users.js";
export type {
  AddQuoteLineItemPayload,
  AddQuoteNotePayload,
  AddQuoteTermsBlockPayload,
  ChangeQuoteStatusPayload,
  CreateQuotePayload,
  DuplicateQuotePayload,
  ListQuotesQuery,
  QuoteListSortBy,
  QuoteHeaderDetailsPayload,
  QuoteLineItemInputPayload,
  ReorderQuoteLineItemsPayload,
  SetQuoteInternalNotesPayload,
  UpdateQuoteNotePayload,
  UpdateQuotePayload,
  EnableWorkspacePayload,
  SetTenderOutcomePayload,
  RejectQuotePayload,
  RejectQuoteResult,
  AcceptProjectMode,
  AcceptQuotePayload,
  AcceptQuoteResult,
  ConvertQuotePayload,
  BulkUpdateTimeEntryBillablePayload,
  SyncWalkthroughToQuotePayload,
  SendQuotePayload,
} from "./endpoints/quotes.js";
export type {
  QuoteTemplateDto,
  CreateQuoteTemplatePayload,
  UpdateQuoteTemplatePayload,
} from "./endpoints/quote-templates.js";
export type {
  CreateCustomReportPayload,
  UpdateCustomReportPayload,
} from "./endpoints/custom-reports.js";
export { SupplierApi } from "./endpoints/supplier.js";
export { SupplierCatalogApi } from "./endpoints/supplier-catalog.js";
export type {
  CatalogSubscriptionStatus,
  CatalogUploadResultDto,
  PagedSupplierCatalogItemsDto,
  SearchSupplierCatalogItemsQuery,
  SupplierCatalogDto,
  SupplierCatalogItemDto,
  SupplierCatalogSubscriptionDto,
  SupplierCustomerPriceDto,
  SupplierCustomerPriceSummaryDto,
} from "./endpoints/supplier-catalog.js";
export { CatalogOffersApi } from "./endpoints/catalog-offers.js";
export type { CatalogOfferDto, CatalogOfferStatus } from "./endpoints/catalog-offers.js";
export type {
  AcceptSupplierAccessPayload,
  AcceptSupplierAccessResultDto,
  CreatedSupplierCustomerDto,
  CreateSupplierCustomerPayload,
  RequestSupplierAccessResultDto,
  SupplierCustomerDto,
  SupplierCustomerSummaryDto,
  SupplierDashboardAggregateDto,
  SupplierDashboardCustomerDto,
  SupplierDashboardDto,
  SupplierFollowUpDto,
  SupplierFollowUpKind,
  SupplierKpiKey,
  SupplierKpisDto,
  SupplierPeriod,
  SupplierRestrictedMetric,
  SupplierSummaryAnalysisDto,
  SupplierSummaryProjectDto,
  SupplierCustomerOrigin,
  SupplierCustomerStatus,
  SellerCustomerTransferDto,
  TransferSellerCustomersResultDto,
  TransferSupplierGrantResultDto,
} from "./endpoints/supplier.js";
export { DpaApi } from "./endpoints/dpa.js";
export type {
  AcceptDpaPayload,
  DpaAcceptanceInput,
  DpaAcceptanceSummaryDto,
  DpaBlockDto,
  DpaDocumentDto,
  DpaNoticeInput,
  DpaNoticeOutcomeStatus,
  DpaRecipientDto,
  DpaSectionDto,
  DpaStatusDto,
  RecordDpaNoticesPayload,
  RecordDpaNoticesResultDto,
} from "./endpoints/dpa.js";
export { GoalsApi } from "./endpoints/goals.js";
export type {
  ApproveTimeEntryPayload,
  CreateTimeEntryPayload,
  ListTimeEntriesQuery,
  RejectTimeEntryPayload,
  StopTimeEntryPayload,
  UpdateTimeEntryPayload,
} from "./endpoints/time-entries.js";
export type {
  ListTimeTypesQuery,
  CreateTimeTypePayload,
  UpdateTimeTypePayload,
} from "./endpoints/time-types.js";
export type {
  UpdateUserPayPayload,
  UserPayRowInput,
} from "./endpoints/user-pay.js";
export type {
  CreateTimeplanPhasePayload,
  CreateTimeplanPhaseNotePayload,
  ReorderTimeplanPhasesPayload,
  TimeplanPhaseOrderInput,
  UpdateTimeplanPhasePayload,
} from "./endpoints/timeplan.js";
export { GuestsApi } from "./endpoints/guests.js";

export class MesterplanApi {
  readonly http: HttpClient;
  readonly activity: ActivityApi;
  readonly dashboard: DashboardApi;
  readonly aftalesedler: AftalesedlerApi;
  readonly me: MeApi;
  readonly notifications: NotificationsApi;
  readonly announcements: AnnouncementsApi;
  readonly users: UsersApi;
  readonly companies: CompaniesApi;
  readonly cvr: CvrApi;
  readonly communicationMessages: CommunicationMessagesApi;
  readonly recipientLists: RecipientListsApi;
  readonly support: SupportApi;
  readonly creditorInvoices: CreditorInvoicesApi;
  readonly customSkills: CustomSkillsApi;
  readonly customers: CustomersApi;
  readonly customerScores: CustomerScoresApi;
  readonly defects: DefectsApi;
  readonly guests: GuestsApi;
  readonly projects: ProjectsApi;
  readonly assignments: AssignmentsApi;
  readonly assets: AssetsApi;
  /** Ejendomme — buildings, their units and their access information. */
  readonly properties: PropertiesApi;
  readonly bidParticipants: BidParticipantsApi;
  readonly bilag: BilagApi;
  readonly blueprints: BlueprintsApi;
  readonly analysisDrafts: AnalysisDraftsApi;
  readonly tenders: TendersApi;
  readonly blueprintCredits: BlueprintCreditsApi;
  readonly platformAdmin: PlatformAdminApi;
  readonly klsChecklists: KlsChecklistsApi;
  readonly klsCompetencies: KlsCompetenciesApi;
  readonly klsDeviations: KlsDeviationsApi;
  readonly klsEquipment: KlsEquipmentApi;
  readonly klsEvaluations: KlsEvaluationsApi;
  readonly klsInspections: KlsInspectionsApi;
  readonly ksProjectItems: KsProjectItemsApi;
  readonly ksTemplates: KsTemplatesApi;
  readonly calculationTemplates: CalculationTemplatesApi;
  readonly verificationTemplates: VerificationTemplatesApi;
  readonly verifications: VerificationsApi;
  readonly integrations: IntegrationsApi;
  readonly invoices: InvoicesApi;
  readonly klsAi: KlsAiApi;
  readonly klsTasks: KlsTasksApi;
  readonly materials: MaterialsApi;
  readonly shoppingList: ShoppingListApi;
  readonly partnerInvitations: PartnerInvitationsApi;
  readonly paymentTermsPresets: PaymentTermsPresetsApi;
  readonly payroll: PayrollApi;
  readonly permissions: PermissionsApi;
  readonly supplierAccess: SupplierAccessApi;
  readonly termsTemplates: TermsTemplatesApi;
  readonly trades: TradesApi;
  readonly walkthroughs: WalkthroughsApi;
  readonly walkthroughTemplates: WalkthroughTemplatesApi;
  readonly documents: DocumentsApi;
  readonly financials: FinancialsApi;
  readonly photos: PhotosApi;
  readonly priceLibrary: PriceLibraryApi;
  readonly priceLists: PriceListsApi;
  readonly products: ProductsApi;
  readonly projectBudgets: ProjectBudgetsApi;
  readonly caseEconomy: CaseEconomyApi;
  readonly economy: CompanyEconomyApi;
  readonly projectCalculations: ProjectCalculationsApi;
  readonly projectTilbudsliste: ProjectTilbudslisteApi;
  readonly quotes: QuotesApi;
  readonly quoteTemplates: QuoteTemplatesApi;
  readonly customReports: CustomReportsApi;
  readonly dpa: DpaApi;
  readonly supplier: SupplierApi;
  readonly supplierCatalog: SupplierCatalogApi;
  readonly catalogOffers: CatalogOffersApi;
  readonly goals: GoalsApi;
  readonly settings: SettingsApi;
  readonly teams: TeamsApi;
  readonly timeEntries: TimeEntriesApi;
  readonly timeTypes: TimeTypesApi;
  readonly timeplan: TimeplanApi;
  readonly leave: LeaveApi;
  readonly projectProgressInvoices: ProjectProgressInvoicesApi;
  readonly userPay: UserPayApi;

  constructor(options: HttpClientOptions) {
    this.http = new HttpClient(options);
    this.activity = new ActivityApi(this.http);
    this.dashboard = new DashboardApi(this.http);
    this.aftalesedler = new AftalesedlerApi(this.http);
    this.me = new MeApi(this.http);
    this.notifications = new NotificationsApi(this.http);
    this.announcements = new AnnouncementsApi(this.http);
    this.users = new UsersApi(this.http);
    this.companies = new CompaniesApi(this.http);
    this.cvr = new CvrApi(this.http);
    this.communicationMessages = new CommunicationMessagesApi(this.http);
    this.recipientLists = new RecipientListsApi(this.http);
    this.support = new SupportApi(this.http);
    this.creditorInvoices = new CreditorInvoicesApi(this.http);
    this.customSkills = new CustomSkillsApi(this.http);
    this.customers = new CustomersApi(this.http);
    this.customerScores = new CustomerScoresApi(this.http);
    this.defects = new DefectsApi(this.http);
    this.guests = new GuestsApi(this.http);
    this.projects = new ProjectsApi(this.http);
    this.assignments = new AssignmentsApi(this.http);
    this.assets = new AssetsApi(this.http);
    this.properties = new PropertiesApi(this.http);
    this.bidParticipants = new BidParticipantsApi(this.http);
    this.bilag = new BilagApi(this.http);
    this.blueprints = new BlueprintsApi(this.http);
    this.analysisDrafts = new AnalysisDraftsApi(this.http);
    this.tenders = new TendersApi(this.http);
    this.blueprintCredits = new BlueprintCreditsApi(this.http);
    this.platformAdmin = new PlatformAdminApi(this.http);
    this.klsChecklists = new KlsChecklistsApi(this.http);
    this.klsCompetencies = new KlsCompetenciesApi(this.http);
    this.klsDeviations = new KlsDeviationsApi(this.http);
    this.klsEquipment = new KlsEquipmentApi(this.http);
    this.klsEvaluations = new KlsEvaluationsApi(this.http);
    this.klsInspections = new KlsInspectionsApi(this.http);
    this.ksProjectItems = new KsProjectItemsApi(this.http);
    this.ksTemplates = new KsTemplatesApi(this.http);
    this.calculationTemplates = new CalculationTemplatesApi(this.http);
    this.verificationTemplates = new VerificationTemplatesApi(this.http);
    this.verifications = new VerificationsApi(this.http);
    this.integrations = new IntegrationsApi(this.http);
    this.invoices = new InvoicesApi(this.http);
    this.klsAi = new KlsAiApi(this.http);
    this.klsTasks = new KlsTasksApi(this.http);
    this.materials = new MaterialsApi(this.http);
    this.shoppingList = new ShoppingListApi(this.http);
    this.partnerInvitations = new PartnerInvitationsApi(this.http);
    this.paymentTermsPresets = new PaymentTermsPresetsApi(this.http);
    this.payroll = new PayrollApi(this.http);
    this.permissions = new PermissionsApi(this.http);
    this.supplierAccess = new SupplierAccessApi(this.http);
    this.termsTemplates = new TermsTemplatesApi(this.http);
    this.trades = new TradesApi(this.http);
    this.walkthroughs = new WalkthroughsApi(this.http);
    this.walkthroughTemplates = new WalkthroughTemplatesApi(this.http);
    this.documents = new DocumentsApi(this.http);
    this.financials = new FinancialsApi(this.http);
    this.photos = new PhotosApi(this.http);
    this.priceLibrary = new PriceLibraryApi(this.http);
    this.priceLists = new PriceListsApi(this.http);
    this.products = new ProductsApi(this.http);
    this.projectBudgets = new ProjectBudgetsApi(this.http);
    this.caseEconomy = new CaseEconomyApi(this.http);
    this.economy = new CompanyEconomyApi(this.http);
    this.projectCalculations = new ProjectCalculationsApi(this.http);
    this.projectTilbudsliste = new ProjectTilbudslisteApi(this.http);
    this.quotes = new QuotesApi(this.http);
    this.quoteTemplates = new QuoteTemplatesApi(this.http);
    this.customReports = new CustomReportsApi(this.http);
    this.dpa = new DpaApi(this.http);
    this.supplier = new SupplierApi(this.http);
    this.supplierCatalog = new SupplierCatalogApi(this.http);
    this.catalogOffers = new CatalogOffersApi(this.http);
    this.goals = new GoalsApi(this.http);
    this.settings = new SettingsApi(this.http);
    this.teams = new TeamsApi(this.http);
    this.timeEntries = new TimeEntriesApi(this.http);
    this.timeTypes = new TimeTypesApi(this.http);
    this.timeplan = new TimeplanApi(this.http);
    this.leave = new LeaveApi(this.http);
    this.projectProgressInvoices = new ProjectProgressInvoicesApi(this.http);
    this.userPay = new UserPayApi(this.http);
  }
}

export type { AnalysisDraftDto, AnalysisDraftFileDto, AnalysisDraftUploadFile, AnalysisDraftUploadPlan, CreateAnalysisDraftRequest, UpdateAnalysisDraftRequest } from "./types/analysis-drafts.js";
